import { Link } from '@/ui-components/atoms/Link/Link';
import { Text } from '@/ui-components/atoms/Text/Text';
import { Button } from '@/ui-components/atoms/Button/Button';
import { ModalNavWrapper } from '@/ui-components/organisms/Snapshot/ModalNavWrapper/ModalNavWrapper';
import { SnapshotAppFooter } from '@/ui-components/organisms/Snapshot/SnapshotAppFooter/SnapshotAppFooter';
import { SnapshotDivider } from '@/ui-components/organisms/Snapshot/SnapshotDivider/SnapshotDivider';
import { buildCareUrlFromBaseUrl } from '@/services/careUrls';
import { getCareBaseUrl } from '@/services/runtimeConfig';
import styles from './inAppPurchasePopover.module.scss';

const APPLE_INSTRUCTIONS_URL = 'https://www.siriusxm.com/help/manage-or-cancel-service';
const CARE_FORGOT_PASSWORD_PATH = '/account/credentials/forgot-password?';
const CARE_DELETE_ACCOUNT_PATH = '/accountcenter/delete/delete-account';

export type InAppPurchasePopoverVariant =
    | 'canada-managed'
    | 'streaming-identity'
    | 'apple-managed'
    | 'amazon-managed'
    | 'google-managed'
    | 'third-party-managed'
    | 'incomplete-transaction'
    | 'expired-subscription';

export interface InAppPurchasePopoverProps {
    chatClickHandler?: React.MouseEventHandler<HTMLAnchorElement>;
    embedded?: boolean;
    forgotPasswordHref?: string;
    instructionsHref?: string;
    onDismissClick?: React.MouseEventHandler<HTMLAnchorElement>;
    subscribeHref?: string;
    variant: InAppPurchasePopoverVariant;
    onBackClick?: () => void;
}

function preventLinkNavigation(event: React.MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
}

interface VariantContentProps {
    chatClickHandler?: React.MouseEventHandler<HTMLAnchorElement>;
    deleteAccountHref?: string;
    forgotPasswordHref?: string;
    includeFooter?: boolean;
    instructionsHref?: string;
    onDismissClick?: React.MouseEventHandler<HTMLAnchorElement>;
    subscribeHref?: string;
}

function ManagedByAppleContent({
    chatClickHandler = preventLinkNavigation,
    forgotPasswordHref = CARE_FORGOT_PASSWORD_PATH,
    includeFooter = true,
    instructionsHref = APPLE_INSTRUCTIONS_URL,
}: VariantContentProps): React.JSX.Element {
    return (
        <>
            <div className={styles.section}>
                <Text tag="h6" variant="h6" className={styles.headline}>
                    Looks like one or more of your subscriptions are managed by Apple.
                </Text>
                <div className={styles.bodyCopy}>
                    <Text tag="p" variant="text-medium" className={styles.copy}>
                        You can manage those subscriptions directly with Apple, or in some cases via the app store or
                        your device settings.{' '}
                        <Link href={instructionsHref} className={styles.inlineLink}>
                            View instructions
                        </Link>
                    </Text>
                    <Text tag="p" variant="text-medium" className={styles.copy}>
                        For subscriptions acquired directly from SiriusXM,{' '}
                        <Link href="" onClick={chatClickHandler} className={styles.inlineLink}>
                            chat with an agent
                        </Link>{' '}
                        for help logging in.
                    </Text>
                </div>
            </div>
            <SnapshotDivider />
            <div className={styles.section}>
                <Text tag="p" variant="text-medium" className={styles.strongCopy}>
                    Need a password to listen on a specific device?
                </Text>
                <Link href={forgotPasswordHref} className={styles.textLink}>
                    Create password
                </Link>
            </div>
            {includeFooter ? <SnapshotAppFooter /> : null}
        </>
    );
}

function ManagedByGoogleContent({
    chatClickHandler = preventLinkNavigation,
    deleteAccountHref = CARE_DELETE_ACCOUNT_PATH,
    forgotPasswordHref = CARE_FORGOT_PASSWORD_PATH,
    includeFooter = true,
    instructionsHref = APPLE_INSTRUCTIONS_URL,
}: VariantContentProps): React.JSX.Element {
    return (
        <>
            <div className={styles.section}>
                <Text tag="h6" variant="h6" className={styles.headline}>
                    Looks like one or more of your subscriptions are managed by Google.
                </Text>
                <div className={styles.bodyCopy}>
                    <Text tag="p" variant="text-medium" className={styles.copy}>
                        You can manage those subscriptions directly with Google, or in some cases via the app store or
                        your device settings.{' '}
                        <Link href={instructionsHref} className={styles.inlineLink}>
                            View instructions
                        </Link>
                    </Text>
                    <Text tag="p" variant="text-medium" className={styles.copy}>
                        Want to delete your account?{' '}
                        <Link href={deleteAccountHref} className={styles.inlineLink}>
                            Learn more
                        </Link>
                    </Text>
                    <Text tag="p" variant="text-medium" className={styles.copy}>
                        For subscriptions acquired directly from SiriusXM,{' '}
                        <Link href="" onClick={chatClickHandler} className={styles.inlineLink}>
                            chat with an agent
                        </Link>{' '}
                        for help logging in.
                    </Text>
                </div>
            </div>
            <SnapshotDivider />
            <div className={styles.section}>
                <Text tag="p" variant="text-medium" className={styles.strongCopy}>
                    Need a password to listen on a specific device?
                </Text>
                <Link href={forgotPasswordHref} className={styles.textLink}>
                    Create password
                </Link>
            </div>
            {includeFooter ? <SnapshotAppFooter /> : null}
        </>
    );
}

function ManagedByAmazonContent({
    chatClickHandler = preventLinkNavigation,
    forgotPasswordHref = CARE_FORGOT_PASSWORD_PATH,
    includeFooter = true,
    instructionsHref = APPLE_INSTRUCTIONS_URL,
}: VariantContentProps): React.JSX.Element {
    return (
        <>
            <div className={styles.section}>
                <Text tag="h6" variant="h6" className={styles.headline}>
                    Looks like one or more of your subscriptions are managed by Amazon.
                </Text>
                <div className={styles.bodyCopy}>
                    <Text tag="p" variant="text-medium" className={styles.copy}>
                        You can manage those subscriptions directly with Amazon, or in some cases via the app store or
                        your device settings.{' '}
                        <Link href={instructionsHref} className={styles.inlineLink}>
                            View instructions
                        </Link>
                    </Text>
                    <Text tag="p" variant="text-medium" className={styles.copy}>
                        For subscriptions acquired directly from SiriusXM,{' '}
                        <Link href="" onClick={chatClickHandler} className={styles.inlineLink}>
                            chat with an agent
                        </Link>{' '}
                        for help logging in.
                    </Text>
                </div>
            </div>
            <SnapshotDivider />
            <div className={styles.section}>
                <Text tag="p" variant="text-medium" className={styles.strongCopy}>
                    Need a password to listen on a specific device?
                </Text>
                <Link href={forgotPasswordHref} className={styles.textLink}>
                    Create password
                </Link>
            </div>
            {includeFooter ? <SnapshotAppFooter /> : null}
        </>
    );
}

function ManagedByThirdPartyContent({
    includeFooter = true,
    subscribeHref = '/player/welcome',
}: VariantContentProps): React.JSX.Element {
    return (
        <>
            <div className={styles.section}>
                <Text tag="h6" variant="h6" className={styles.headline}>
                    Looks like this subscription is managed by a third-party provider.
                </Text>
                <div className={styles.bodyCopy}>
                    <Text tag="p" variant="text-medium" className={styles.copy}>
                        Subscriptions purchased though the SiriusXM App or another retailer, must be managed directly
                        with that provider.
                    </Text>
                    <Link href={subscribeHref} className={styles.textLink}>
                        Log in to start listening
                    </Link>
                </div>
            </div>
            {includeFooter ? <SnapshotAppFooter /> : null}
        </>
    );
}

function StreamingIdentityContent({ subscribeHref }: VariantContentProps): React.JSX.Element {
    const accountLoginHref = buildCareUrlFromBaseUrl(getCareBaseUrl(), '/accountcenter/login');

    return (
        <div className={styles.sectionWithActions}>
            <div>
                <Text tag="h6" variant="h6" className={`${styles.headline} ${styles.informationHeadline}`}>
                    Did you mean to start listening?
                </Text>
                <Text tag="p" variant="text-medium" className={styles.copy}>
                    The login you entered is for streaming. To manage your account, please sign in with your Online
                    Account login. Or, chat with an agent for additional help.
                </Text>
            </div>
            <div className={`${styles.actionGroup} ${styles.streamingActions}`}>
                <Button type="button" size="small" variant="primary" fitting="fill-container" href={accountLoginHref}>
                    Enter account login
                </Button>
                <Button type="button" size="small" variant="secondary" fitting="fill-container" href={subscribeHref}>
                    Start listening
                </Button>
            </div>
        </div>
    );
}

function CanadianAccountContent({ subscribeHref }: VariantContentProps): React.JSX.Element {
    return (
        <div className={`${styles.sectionWithActions} ${styles.informationContent}`}>
            <div>
                <Text tag="h6" variant="h6" className={`${styles.headline} ${styles.informationHeadline}`}>
                    Looks like you’re a SiriusXM Canada subscriber
                </Text>
                <Text tag="p" variant="text-medium" className={styles.copy}>
                    To sign in, please visit the Canadian account center.
                </Text>
            </div>
            <Button type="button" size="small" variant="primary" fitting="fill-container" href={subscribeHref}>
                Go to SiriusXM Canada
            </Button>
        </div>
    );
}

function IncompleteTransactionContent({
    onDismissClick = preventLinkNavigation,
    subscribeHref,
}: VariantContentProps): React.JSX.Element {
    return (
        <>
            <div className={styles.sectionWithActions}>
                <Text tag="h6" variant="h6" className={styles.headline}>
                    Looks like you were interested in subscribing to SiriusXM but didn&apos;t complete the transaction.
                </Text>
                <div className={styles.actionGroup}>
                    <Button type="button" size="small" variant="primary" fitting="fill-container" href={subscribeHref}>
                        Subscribe now
                    </Button>
                    <Link href="#" onClick={onDismissClick} className={styles.textLinkCentered}>
                        No thanks
                    </Link>
                </div>
            </div>
        </>
    );
}

function ExpiredSubscriptionContent({ subscribeHref }: VariantContentProps): React.JSX.Element {
    return (
        <>
            <div className={styles.sectionWithActions}>
                <div className={styles.bodyCopy}>
                    <Text tag="h6" variant="h6" className={styles.headline}>
                        Your subscription expired
                    </Text>
                    <Text tag="p" variant="text-medium" className={styles.copy}>
                        But this doesn&apos;t have to be the end. You can subscribe to a new plan.
                    </Text>
                </div>
                <div className={styles.actionGroup}>
                    <Button type="button" size="small" variant="primary" fitting="fill-container" href={subscribeHref}>
                        Subscribe
                    </Button>
                </div>
            </div>
        </>
    );
}

function VariantContent({
    chatClickHandler,
    deleteAccountHref,
    forgotPasswordHref,
    includeFooter = true,
    instructionsHref,
    onDismissClick,
    subscribeHref,
    variant,
}: VariantContentProps & { variant: InAppPurchasePopoverVariant }): React.JSX.Element {
    if (variant === 'canada-managed') {
        return <CanadianAccountContent subscribeHref={subscribeHref} />;
    }

    if (variant === 'streaming-identity') {
        return <StreamingIdentityContent subscribeHref={subscribeHref} />;
    }

    if (variant === 'apple-managed') {
        return (
            <ManagedByAppleContent
                chatClickHandler={chatClickHandler}
                forgotPasswordHref={forgotPasswordHref}
                includeFooter={includeFooter}
                instructionsHref={instructionsHref}
            />
        );
    }

    if (variant === 'google-managed') {
        return (
            <ManagedByGoogleContent
                chatClickHandler={chatClickHandler}
                deleteAccountHref={deleteAccountHref}
                forgotPasswordHref={forgotPasswordHref}
                includeFooter={includeFooter}
                instructionsHref={instructionsHref}
            />
        );
    }

    if (variant === 'amazon-managed') {
        return (
            <ManagedByAmazonContent
                chatClickHandler={chatClickHandler}
                forgotPasswordHref={forgotPasswordHref}
                includeFooter={includeFooter}
                instructionsHref={instructionsHref}
            />
        );
    }

    if (variant === 'third-party-managed') {
        return <ManagedByThirdPartyContent includeFooter={includeFooter} subscribeHref={subscribeHref} />;
    }

    if (variant === 'incomplete-transaction') {
        return <IncompleteTransactionContent onDismissClick={onDismissClick} subscribeHref={subscribeHref} />;
    }

    return <ExpiredSubscriptionContent subscribeHref={subscribeHref} />;
}

export function InAppPurchasePopover({
    chatClickHandler,
    embedded = false,
    forgotPasswordHref,
    instructionsHref,
    onDismissClick,
    subscribeHref,
    variant,
    onBackClick,
}: InAppPurchasePopoverProps): React.JSX.Element {
    const careBaseUrl = getCareBaseUrl();
    const resolvedForgotPasswordHref =
        forgotPasswordHref ?? buildCareUrlFromBaseUrl(careBaseUrl, CARE_FORGOT_PASSWORD_PATH);
    const resolvedDeleteAccountHref = buildCareUrlFromBaseUrl(careBaseUrl, CARE_DELETE_ACCOUNT_PATH);
    const content = (
        <VariantContent
            chatClickHandler={chatClickHandler}
            deleteAccountHref={resolvedDeleteAccountHref}
            forgotPasswordHref={resolvedForgotPasswordHref}
            includeFooter={!embedded}
            instructionsHref={instructionsHref}
            onDismissClick={onDismissClick}
            subscribeHref={subscribeHref}
            variant={variant}
        />
    );

    if (embedded) {
        return content;
    }

    return (
        <ModalNavWrapper showBackButton onBackClick={onBackClick}>
            {content}
        </ModalNavWrapper>
    );
}
